import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { Plus, Edit2, Trash2, Save, X } from "lucide-react";
import {
    getAllEmploisRequest,
    createEmploiRequest,
    updateEmploiRequest,
    deleteEmploiRequest
} from "../../redux/actions/emploiActions";

export default function EmploisTab() {
    const dispatch = useDispatch();
    const { emplois = [], loading } = useSelector((state) => state.emploi || {});
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ code: "", name: "", description: "" });
    const [q, setQ] = useState("");

    useEffect(() => {
        dispatch(getAllEmploisRequest());
    }, [dispatch]);

    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        return (emplois || []).filter((e) => {
            return !t || (e.code || "").toLowerCase().includes(t) || (e.name || "").toLowerCase().includes(t) || (e.description || "").toLowerCase().includes(t);
        });
    }, [emplois, q]);

    const openCreate = () => {
        setEditing(null);
        setForm({ code: "", name: "", description: "" });
        setIsModalOpen(true);
    };

    const openEdit = (e) => {
        setEditing(e);
        setForm({ code: e.code || "", name: e.name || "", description: e.description || "" });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditing(null);
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const submit = (e) => {
        e.preventDefault();
        if (!form.code?.trim()) { Swal.fire({ icon: "warning", text: "Code obligatoire." }); return; }
        if (!form.name?.trim()) { Swal.fire({ icon: "warning", text: "Nom obligatoire." }); return; }
        
        const payload = {
            code: form.code.trim(),
            name: form.name.trim(),
            description: form.description?.trim() || ""
        };

        if (editing?.id) {
            dispatch(updateEmploiRequest(editing.id, payload, closeModal));
        } else {
            dispatch(createEmploiRequest(payload, closeModal));
        }
    };

    const confirmDelete = (e) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer l'emploi "${e.name}" ? Tous les postes rattachés perdront leur référence à cet emploi.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
            confirmButtonColor: "#dc3545"
        }).then((r) => {
            if (r.isConfirmed) dispatch(deleteEmploiRequest(e.id));
        });
    };

    return (
        <div style={{ padding: "10px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <input 
                    type="text" 
                    className="crSearch" 
                    value={q} 
                    onChange={(e) => setQ(e.target.value)} 
                    placeholder="Code, nom, description..." 
                    style={{ width: "300px" }}
                />
                <button className="crBtn crBtn--primary" onClick={openCreate} type="button">
                    <Plus size={16} /> Ajouter un emploi
                </button>
            </div>

            <div className="crWrap">
                <div className="crCard">
                    <div className="crBody">
                        <div className="crTableWrap">
                            <table className="crTable">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Nom de l'emploi</th>
                                        <th>Description</th>
                                        <th>Date création</th>
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={5}><div className="crLoading"><div className="crSpinner" /><span>Chargement...</span></div></td></tr>
                                    ) : filtered.length === 0 ? (
                                        <tr><td colSpan={5} className="crEmptyCell">Aucun emploi trouvé</td></tr>
                                    ) : filtered.map((e) => (
                                        <tr key={e.id}>
                                            <td><span className="crStrong">{e.code}</span></td>
                                            <td style={{ fontWeight: 700, color: "#2f4595", fontSize: 12 }}>{e.name}</td>
                                            <td className="crMuted">{e.description || "—"}</td>
                                            <td className="crMuted" style={{ fontSize: 11 }}>
                                                {e.createdAt ? new Date(e.createdAt).toLocaleDateString("fr-FR") : "—"}
                                            </td>
                                            <td className="is-right">
                                                <div className="crRowActions">
                                                    <button className="crIconBtn" title="Modifier" onClick={() => openEdit(e)} type="button">
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button className="crIconBtn crIconBtn--danger" title="Supprimer" onClick={() => confirmDelete(e)} type="button">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <div className="crModal" role="dialog" aria-modal="true" onMouseDown={closeModal}>
                    <div className="crModal__panel crModal__panel--lg" onMouseDown={(ev) => ev.stopPropagation()}>
                        <div className="crModal__header">
                            <div className="crModal__title">{editing ? "Modifier l'emploi" : "Ajouter un emploi"}</div>
                            <button className="crIconBtn" type="button" onClick={closeModal} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>
                        <form className="crModal__body crFormContent" onSubmit={submit}>
                            <div className="crFormSection">
                                <div className="crFormGrid">
                                    <div className="crField">
                                        <label>Code *</label>
                                        <input name="code" value={form.code} onChange={onChange} placeholder="Ex : EMP-01" maxLength={50} required />
                                    </div>
                                    <div className="crField">
                                        <label>Nom de l'emploi *</label>
                                        <input name="name" value={form.name} onChange={onChange} placeholder="Ex : Management" maxLength={200} required />
                                    </div>
                                    <div className="crField crField--full">
                                        <label>Description</label>
                                        <textarea name="description" value={form.description} onChange={onChange} rows={3} placeholder="Description..." />
                                    </div>
                                </div>
                            </div>
                        </form>
                        <div className="crModal__footer">
                            <button className="crBtn crBtn--ghost" onClick={closeModal} type="button">Annuler</button>
                            <button className="crBtn crBtn--primary" onClick={submit} type="button">
                                <Save size={16} /> {editing ? "Enregistrer" : "Créer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
